import * as React from 'react';
import { action, observable, IObservableArray } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as FlipMove from 'react-flip-move';

// Assets
const AwardSVG = require('../../assets/illustrations/award.svg') as string;

// Services
import {
  myNotifications,
  IMyNotificationsResponse,
  myNotificationsArchive,
  IMyNotificationsArchiveResponse
} from '../../services/user';
import {
  createQuestionAnswer,
  IOption,
  getQuestionComments,
  IGetQuestionsCommentResponse,
  createQuestionComments
} from '../../services/question';

// Types
import { IStore } from '../../store/Store';

export type INotification = IMyNotificationsResponse;
export type INotificationAll = IMyNotificationsArchiveResponse;
export type INotificationOption = IOption;
export type IComment = IGetQuestionsCommentResponse;

// Components
import Box from '../../components/Box';
import CommentsDialog from '../../components/CommentsDialog';
import { CircularProgress } from '../../components/Progress';
import StatusCard from '../../components/StatusCard';
import Typography from '../../components/Typography';

import NotifcationListItem from './NotifcationListItem';
import NotificationListPendingItem from './NotificationListPendingItem';
import QuestionDialog from '../AppNetworkQuestions/QuestionDialog';

// Component
interface IProps {
  store?: IStore;
}

class NotificationList extends React.Component<IProps> {
  @observable
  questionDialogState: {
    open: boolean;
    questionId: string | null;
    networkId: string | null;
  } = {
    open: false,
    questionId: null,
    networkId: null
  };

  @observable
  commentDialogState: {
    open: boolean;
    questionId: string | null;
    networkId: string | null;
  } = {
    open: false,
    questionId: null,
    networkId: null
  };

  @observable notifications: INotification[] | null = null;
  @observable notificationsAll: INotificationAll[] | null = null;
  @observable comments: IComment[] | null = null;

  componentDidMount() {
    this.getNotifications();
  }

  async getComments() {
    this.comments = await getQuestionComments({
      params: {
        networkId: this.commentDialogState.networkId!
      },
      query: {
        questionId: this.commentDialogState.questionId!
      }
    });
  }

  async createComment(comment: string) {
    await createQuestionComments({
      params: {
        networkId: this.commentDialogState.networkId!
      },
      query: {
        questionId: this.commentDialogState.questionId!
      },
      body: {
        comment: comment
      }
    });

    this.getComments();
  }

  getNotifications = async () => {
    myNotifications().then(res => {
      this.notifications = res;
    });
    myNotificationsArchive().then(res => {
      this.notificationsAll = res;
    });
  };

  handleAnswer = async (
    { optionId }: IOption,
    notification: IMyNotificationsResponse
  ) => {
    await createQuestionAnswer({
      params: { networkId: notification.networkId },
      query: {
        questionId: notification.questionId,
        optionId
      }
    });
    (this.notifications as IObservableArray<IMyNotificationsResponse>).remove(
      notification
    );
    this.props.store!.meStore.updateNotificationCount();
  };

  @action
  handleRequestComment = (questionId: string, networkId: string) => () => {
    this.commentDialogState.open = true;
    this.commentDialogState.questionId = questionId;
    this.commentDialogState.networkId = networkId;
    this.getComments();
  };

  handleCommentDialogSubmit = async (comment: string) => {
    await this.createComment(comment);
  };

  handleCommentDialogClose = () => {
    this.commentDialogState.open = false;
  };

  @action
  handleCommentDialogExited = () => {
    this.commentDialogState.open = false;
    this.commentDialogState.questionId = null;
    this.commentDialogState.networkId = null;
    this.comments = null;
  };

  @action
  handleRequestQuestion = (questionId: string, networkId: string) => () => {
    this.questionDialogState.open = true;
    this.questionDialogState.questionId = questionId;
    this.questionDialogState.networkId = networkId;
  };

  @action
  handleQuestionDialogClose = () => {
    this.questionDialogState.open = false;
    this.questionDialogState.questionId = null;
    this.questionDialogState.networkId = null;
    this.comments = null;
  };

  @action
  handleQuestionDialogDeleted = () => {
    this.getNotifications();
    this.handleQuestionDialogClose();
  };

  render() {
    if (this.notifications === null || this.notificationsAll === null) {
      return (
        <Box pa={5} align="center">
          <CircularProgress />
        </Box>
      );
    }
    const role = this.props.store!.networkStore.person!.role;
    return (
      <div>
        <FlipMove
          duration={500}
          easing="cubic-bezier(.36,.74,.46,1)"
          enterAnimation="fade"
          leaveAnimation="fade"
        >
          {this.notifications.length ? (
            this.notifications.map(item => (
              <div key={item.questionId}>
                <NotificationListPendingItem
                  attention={true}
                  question={item}
                  onAnswer={this.handleAnswer}
                  onRequestQuestion={this.handleRequestQuestion(
                    item.questionId,
                    item.networkId
                  )}
                  onRequestComment={this.handleRequestComment(
                    item.questionId,
                    item.networkId
                  )}
                />
              </div>
            ))
          ) : (
            <div>
              <StatusCard
                icon={AwardSVG}
                headline={'Congratulations!'}
                subheading={'You have cleared your notification tray.'}
              />
            </div>
          )}
        </FlipMove>
        <Box py={3}>
          <Typography type="body2" color="textSecondary">
            History
          </Typography>
        </Box>
        <FlipMove
          duration={500}
          easing="cubic-bezier(.36,.74,.46,1)"
          enterAnimation="fade"
          leaveAnimation="fade"
        >
          {this.notificationsAll.map(item => (
            <div key={item.questionId}>
              <NotifcationListItem
                question={item}
                onRequestQuestion={this.handleRequestQuestion(
                  item.questionId,
                  item.networkId
                )}
                onRequestComment={this.handleRequestComment(
                  item.questionId,
                  item.networkId
                )}
              />
            </div>
          ))}
        </FlipMove>
        <QuestionDialog
          state={this.questionDialogState}
          onClose={this.handleQuestionDialogClose}
          onDeleted={this.handleQuestionDialogDeleted}
          userRole={role}
        />
        <CommentsDialog
          comments={this.comments}
          state={this.commentDialogState}
          onSubmit={this.handleCommentDialogSubmit}
          onClose={this.handleCommentDialogClose}
          onExited={this.handleCommentDialogExited}
        />
      </div>
    );
  }
}

export default inject('store')(observer(NotificationList));



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/AppNetworkNotifcations/NotificationList.tsx